/**
 * Pure helpers behind step progress ("Mark step N done", per-chapter
 * "x of n done", and Resume) for the shared step views.
 *
 * Flogger chapter-model migration PR-2b (design doc
 * `docs/design/flogger-components-chapter-model-design.md`, section 3,
 * "Step progress (PR-2b), specified"). The React side is
 * `src/components/content/step-progress.tsx`; it is OFF unless a page opts in
 * (`ArticleView stepProgress={{ docId }}`), so tutorials, scenes and lessons
 * never read or write this storage; and even there it shows only after the
 * reader turns on "Track my steps" (owner decision 2026-11-04, see below).
 *
 * Plain `.ts` on purpose: Server Components, scripts and unit tests can import
 * these without pulling in a `'use client'` module.
 *
 * Storage (design doc section 3):
 *   - key   `step-progress:<Sanity _id>` in localStorage, one entry per doc;
 *   - value a JSON array of the done steps' `_key`s (sorted, no duplicates).
 *   - An empty set removes the entry instead of writing `[]`.
 * The old bench key (`bench-progress:<_id>`, values `"<group>:<step>"` index
 * pairs) is deliberately NOT read or migrated: old ticks reset once (design
 * doc sections 0 and 3, review disposition "bench-progress import dropped").
 */
import { defaultLocale, locales } from '@/lib/i18n/config'
import { pickPortableText } from '@/lib/i18n/pickLocale'

/* ------------------------------------------------------------------ */
/*  Storage                                                            */
/* ------------------------------------------------------------------ */

/** localStorage key prefix; the full key is `step-progress:<_id>`. */
export const STEP_PROGRESS_STORAGE_PREFIX = 'step-progress:'

/** `step-progress:<docId>` — docId is the Sanity document `_id`, as given. */
export function stepProgressStorageKey(docId: string): string {
  return `${STEP_PROGRESS_STORAGE_PREFIX}${docId}`
}

/**
 * Parse a stored value into the done set. Tolerant: anything that is not a
 * JSON array yields an empty set, and non-string / empty entries are dropped,
 * so a corrupt or foreign value never breaks the page.
 */
export function parseStepProgress(raw: unknown): Set<string> {
  if (typeof raw !== 'string' || raw === '') return new Set()
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return new Set()
  }
  if (!Array.isArray(parsed)) return new Set()
  const out = new Set<string>()
  for (const v of parsed) if (typeof v === 'string' && v !== '') out.add(v)
  return out
}

/** The value to store, or null when the set is empty (remove the entry). */
export function serializeStepProgress(done: Iterable<string>): string | null {
  const keys = [...new Set(done)].filter((k) => typeof k === 'string' && k !== '').sort()
  return keys.length > 0 ? JSON.stringify(keys) : null
}

/**
 * A new set with `key` marked done (`value` true), not done (false), or
 * flipped (omitted). Never mutates the input.
 */
export function withStepDone(
  done: ReadonlySet<string>,
  key: string,
  value?: boolean,
): Set<string> {
  const next = new Set(done)
  const on = value ?? !next.has(key)
  if (on) next.add(key)
  else next.delete(key)
  return next
}

/* ------------------------------------------------------------------ */
/*  "Track my steps" preference                                        */
/* ------------------------------------------------------------------ */
//
// Owner decision 2026-11-04: a page passing `stepProgress` only makes step
// progress AVAILABLE; the reader's own "Track my steps" choice decides whether
// it shows, and it is OFF until they turn it on. Mirrors the Default Step View
// preference: signed in with an account value → `profiles.step_tracking_enabled`
// (nullable; via /api/account/step-view `{ tracking }`); otherwise (signed
// out, never chosen, or before that column exists) → the browser's saved
// choice below; nothing saved → off.

/** localStorage key for the reader's "Track my steps" choice ('1' / '0'). */
export const STEP_TRACKING_STORAGE_KEY = 'theduchy.step-tracking'

/** The stored value for a choice. */
export function serializeStepTracking(on: boolean): string {
  return on ? '1' : '0'
}

/** A stored choice → true / false, or null when nothing valid is stored. */
export function parseStepTracking(raw: unknown): boolean | null {
  if (raw === '1') return true
  if (raw === '0') return false
  return null
}

/**
 * The first-render value of "Track my steps" (server and first client render
 * alike, so there is no hydration mismatch): the account value when the
 * server read one, else OFF. A browser-saved choice applies only after mount.
 */
export function resolveInitialStepTracking(serverTracking: boolean | null | undefined): boolean {
  return serverTracking === true
}

/* ------------------------------------------------------------------ */
/*  Which steps are tracked                                            */
/* ------------------------------------------------------------------ */

/** The subset of a raw `stepInstruction` entry the progress helpers read. */
export interface ProgressStepLike {
  _key?: unknown
  severity?: unknown
}

/**
 * Whether a step gets a checkbox and counts toward progress.
 *
 * - It needs its own Sanity `_key` (the stable id the done set stores). The
 *   renderer's fallback key (`step-<index>`) shifts when steps are reordered,
 *   so a step without `_key` is not tracked.
 * - A `danger` step is a "don't do this" example, rendered un-numbered in
 *   list mode; it is not a task, so it is never tracked (in any mode).
 */
export function isTrackableStep(step: ProgressStepLike | null | undefined): boolean {
  if (!step || typeof step !== 'object') return false
  if (typeof step._key !== 'string' || step._key === '') return false
  return step.severity !== 'danger'
}

/* ------------------------------------------------------------------ */
/*  Steps per chapter                                                  */
/* ------------------------------------------------------------------ */

/** One tracked step, located for Resume. */
export interface ProgressStep {
  /** The step's Sanity `_key` (what the done set stores). */
  key: string
  /** `_key` of the `stepByStepGrid` block holding it (null if it has none). */
  gridKey: string | null
  /** Index in that block's `steps` array: the carousel index in carousel mode. */
  index: number
}

export interface ChapterSteps {
  /** The ArticleView section id (`Section.id`). */
  sectionId: string
  /** Tracked steps in reading order. */
  steps: ProgressStep[]
}

/** Locale ids a `localePortableText` map is keyed by. */
const LOCALE_KEYS: ReadonlySet<string> = new Set<string>(locales)

/**
 * Whether `obj` is a `localePortableText` map (`{ en: [...], de: [...] }`,
 * e.g. an accordion panel's or a nested chapter's `content`): no block
 * `_type` (or the map type itself), at least one locale key, and nothing but
 * locale keys (with array / empty values) besides Sanity's `_` bookkeeping.
 */
function isLocalePortableTextMap(obj: Record<string, unknown>): boolean {
  const type = obj._type
  if (type !== undefined && type !== 'localePortableText') return false
  let localeKeys = 0
  for (const [k, v] of Object.entries(obj)) {
    if (k.startsWith('_')) continue
    if (!LOCALE_KEYS.has(k)) return false
    if (v != null && !Array.isArray(v)) return false
    localeKeys += 1
  }
  return localeKeys > 0
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Every tracked step in a Portable Text value, in render order. Walks nested
 * arrays and objects (a grid inside an accordion still counts) but never into
 * a `stepByStepGrid`'s own steps, whose rich text holds no further grids.
 *
 * A nested `localePortableText` map (an accordion panel's `content`, a
 * nested chapter's `content`) holds one parallel copy of the blocks per
 * locale, with the same step `_key`s. Only the copy the renderer shows is
 * walked: `locale` through `pickPortableText`'s fallback ladder (the same
 * call `AccordionBlock` / `ChapterBlock` make), so a grid counts once, not
 * once per translation. Steps are also de-duplicated by `_key` (the done set
 * stores keys, so one key is one tick and counts once); pass a shared
 * `seenKeys` to de-duplicate across several values.
 */
export function collectProgressSteps(
  content: unknown,
  locale: string = defaultLocale,
  seenKeys: Set<string> = new Set(),
): ProgressStep[] {
  const out: ProgressStep[] = []
  const seen = new Set<unknown>()
  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object' || seen.has(node)) return
    seen.add(node)
    if (Array.isArray(node)) {
      for (const child of node) walk(child)
      return
    }
    const obj = node as Record<string, any>
    if (obj._type === 'stepByStepGrid') {
      const gridKey = typeof obj._key === 'string' && obj._key !== '' ? obj._key : null
      const steps = Array.isArray(obj.steps) ? obj.steps : []
      steps.forEach((s: ProgressStepLike, index: number) => {
        if (!isTrackableStep(s)) return
        const key = s._key as string
        if (seenKeys.has(key)) return
        seenKeys.add(key)
        out.push({ key, gridKey, index })
      })
      return
    }
    if (isLocalePortableTextMap(obj)) {
      walk(pickPortableText(obj as Record<string, unknown[] | null | undefined>, locale))
      return
    }
    for (const value of Object.values(obj)) walk(value)
  }
  walk(content)
  return out
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Tracked steps per chapter section, in section order. Non-chapter sections
 * (What's next, Comments) are skipped; chapters without tracked steps are kept
 * with an empty list (callers show no count for them). `locale` is the page
 * locale: it picks the shown copy of nested `localePortableText` maps (the
 * chapter `content` itself is already the active-locale array). A step `_key`
 * counts once on the page (where it first appears).
 */
export function buildChapterStepIndex(
  sections: readonly { kind: string; id: string; content?: unknown }[],
  locale: string = defaultLocale,
): ChapterSteps[] {
  const seenKeys = new Set<string>()
  return sections
    .filter((s) => s.kind === 'chapter')
    .map((s) => ({ sectionId: s.id, steps: collectProgressSteps(s.content, locale, seenKeys) }))
}

export interface DoneCount {
  done: number
  total: number
}

/**
 * How many of `steps` are done. Keys in the done set that are not among the
 * steps (a step since deleted) never count, so `done <= total` always.
 */
export function countDoneSteps(
  steps: readonly { key: string }[],
  done: ReadonlySet<string>,
): DoneCount {
  let n = 0
  for (const s of steps) if (done.has(s.key)) n += 1
  return { done: n, total: steps.length }
}

/** Done / total across every chapter. */
export function countAllDoneSteps(
  chapters: readonly ChapterSteps[],
  done: ReadonlySet<string>,
): DoneCount {
  let d = 0
  let t = 0
  for (const c of chapters) {
    const r = countDoneSteps(c.steps, done)
    d += r.done
    t += r.total
  }
  return { done: d, total: t }
}

/**
 * The first step that is not done, in reading order (chapter order, then step
 * order), with its chapter; null when every tracked step is done or there are
 * none. This is what Resume jumps to (the bench's "first unchecked step
 * across groups").
 */
export function findFirstUndoneStep(
  chapters: readonly ChapterSteps[],
  done: ReadonlySet<string>,
): (ProgressStep & { sectionId: string }) | null {
  for (const c of chapters) {
    for (const s of c.steps) {
      if (!done.has(s.key)) return { ...s, sectionId: c.sectionId }
    }
  }
  return null
}

/**
 * Whether the header Resume button shows: progress is on, at least one step
 * is tracked, and at least one is done (the bench showed it only once the
 * reader had ticked something).
 */
export function shouldShowResume(count: DoneCount): boolean {
  return count.total > 0 && count.done > 0
}
